/**
 * "Refresh RevenueCat cohort labels" — the panel's decisions, pure (2026-09-27).
 *
 * Backend: routers/admin_rc_cohort_labels.py + services/cohort_restamp.py (migration
 * 1832). A run is a PREVIEW (how many expired users would get each RevenueCat label;
 * nothing is written) or an APPLY, allowed once per finished preview from the last hour,
 * which RE-READS the current expired users and stamps each one's label now (the preview's
 * ids are not replayed; its counts are what the owner looked at). At
 * most one run at a time. The server enforces every rule below; the panel mirrors them
 * so its buttons say why they are off. Tests: __tests__/rcCohortLabelsLogic.test.ts.
 */

export type RestampMode = 'preview' | 'apply';
export type RestampStatus = 'running' | 'done' | 'failed';

export interface RestampRun {
  id: string;
  mode: RestampMode;
  status: RestampStatus;
  preview_run_id: string | null;
  started_by: string;
  started_at: string;
  heartbeat_at: string;
  finished_at: string | null;
  candidates: number | null;
  processed: number;
  resolved: Record<string, number>;
  stamped: number;
  failed: number;
  error: string | null;
}

/** services/cohort_restamp.PREVIEW_MAX_AGE */
export const PREVIEW_MAX_AGE_MS = 60 * 60 * 1000;

/** services/cohort_restamp.RUN_STALE_AFTER — a "running" run whose heartbeat is older is
 *  gone (its server task was replaced); the server closes it as stalled on the next read. */
export const RUN_STALE_AFTER_MS = 10 * 60 * 1000;

/** What each RevenueCat label means, in the words the owner uses. */
export const LABEL_NAMES: Readonly<Record<string, string>> = {
  paid_expired: 'Lapsed paid subscribers — see "Re-subscribe to continue with your coach"',
  trial_expired: 'Finished free week — see "Your 7 days with Coach Cedar are done"',
  free_month_ended: 'Free month ended',
  grant: 'Complimentary or evaluator access',
  unknown: 'Could not be read — never stamped (counted as failed on apply)',
};

export function labelName(label: string): string {
  return LABEL_NAMES[label] ?? `Moved on since the list was read (${label})`;
}

/** The run in progress — never one whose heartbeat went stale (review of #4042: a run a
 *  deploy cancelled must not keep Preview and Apply off and the panel polling forever). */
export function runningRun(runs: RestampRun[], now: Date = new Date()): RestampRun | null {
  return runs.find((r) => r.status === 'running'
    && now.getTime() - new Date(r.heartbeat_at).getTime() <= RUN_STALE_AFTER_MS) ?? null;
}

/** The newest preview, whatever its status (runs arrive newest first). */
export function latestPreview(runs: RestampRun[]): RestampRun | null {
  return runs.find((r) => r.mode === 'preview') ?? null;
}

export interface ApplyTarget {
  preview: RestampRun | null;
  /** Why Apply is off; null when it is on. */
  reason: string | null;
}

/** Which preview Apply would apply, or why it cannot. */
export function applyTarget(runs: RestampRun[], now: Date = new Date()): ApplyTarget {
  if (runningRun(runs, now)) return { preview: null, reason: 'A run is in progress.' };
  const preview = latestPreview(runs);
  if (!preview || preview.status !== 'done' || !preview.finished_at) {
    return { preview: null, reason: 'Run a preview first.' };
  }
  if (now.getTime() - new Date(preview.finished_at).getTime() > PREVIEW_MAX_AGE_MS) {
    return { preview: null, reason: 'The last preview is more than an hour old — run a new one.' };
  }
  const applied = runs.some((r) => r.mode === 'apply' && r.preview_run_id === preview.id
    && (r.status === 'running' || r.status === 'done'));
  if (applied) return { preview: null, reason: 'The last preview has been applied — run a new one to apply again.' };
  return { preview, reason: null };
}

/** How many users an apply of this preview would stamp (never the unreadable ones). */
export function stampableUsers(preview: RestampRun): number {
  return Object.entries(preview.resolved ?? {})
    .filter(([label]) => label !== 'unknown')
    .reduce((n, [, users]) => n + users, 0);
}

export function countsRows(run: RestampRun): Array<{ label: string; name: string; users: number }> {
  return Object.entries(run.resolved ?? {})
    .map(([label, users]) => ({ label, name: labelName(label), users }))
    .sort((a, b) => b.users - a.users || a.label.localeCompare(b.label));
}

/** One line of progress or outcome. */
export function runLine(run: RestampRun): string {
  const total = run.candidates ?? 0;
  if (run.status === 'running') {
    if (run.candidates === null) return 'Starting…';
    return run.mode === 'preview'
      ? `Checking ${run.processed} of ${total} expired users…`
      : `Stamping ${run.processed} of ${total} expired users… ${run.stamped} stamped, ${run.failed} failed`;
  }
  if (run.status === 'failed') {
    // How far it got — only when the run knew its size (candidates is null until the
    // first progress write, so "after 0 of 0" would be a lie; wave-8 follow-ups 3).
    const where = run.candidates === null ? '' : ` after ${run.processed} of ${total} users`;
    const again = ' Run it again — a repeat stamps the same labels.';
    if (run.error === 'slow_progress') {
      // Swept while its task was alive: it went quiet for longer than the stale window.
      return `Stopped: it took longer than 10 minutes to report progress, so it was closed${where}.${again}`;
    }
    // 'stalled' (swept) and 'stopped' (the task was cancelled by a deploy): the server
    // running it went away mid-run.
    return run.error === 'stalled' || run.error === 'stopped'
      ? `Stopped: the server running it was replaced${where}.${again}`
      : `Failed (${run.error ?? 'error'})${where}.`;
  }
  return run.mode === 'preview'
    ? `Preview: ${total} expired users checked. Nothing was written.`
    : `Applied: ${run.stamped} stamped, ${run.failed} failed, of ${total} expired users.`;
}
